import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Dropdown } from 'primereact/dropdown'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextareaField from '../../../components/ui/form/FormTextareaField'
import FileUpload from '../../../components/ui/FileUpload'
import { DOCUMENT_ACCEPT } from '../../../lib/uploadAccept'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { requiredText } from '../../../lib/forms/rules'
import { useTodoListOptions, type QuickNoteArgs } from '../hooks/useTodoLists'
import type { TodoList } from '../types'

/** Madde metni sınırı Notlarım detayındaki ekleme alanıyla aynı (1.000 karakter — `TodoTaskUpsertDto`). */
const NOTE_MAX = 1000

const schema = z.object({
  name: requiredText(NOTE_MAX),
})

interface Props {
  visible: boolean
  submitting: boolean
  /**
   * Madde tek istekte oluşur; ekler ikinci bir istekle yüklenir (madde id'si gerekir) — hepsi
   * `useQuickAddNote`'un beklediği şekilde tek nesnede verilir.
   */
  onSubmit: (args: QuickNoteArgs) => void
  onHide: () => void
}

/** Paylaşılan listede sahibin adı da gösterilir: aynı adlı iki liste dropdown'da ayırt edilebilsin. */
function listLabel(list: TodoList): string {
  return list.owned || !list.ownerFullName ? list.name : `${list.name} (${list.ownerFullName})`
}

/**
 * **Ana sayfa → Not Ekle** pop-up'ı (123): Notlarım'a, ekranı açmadan madde ekler.
 *
 * <p>Alanlar: <b>Notunuzu Yazınız</b> (zorunlu madde metni) · <b>Liste</b> (zorunlu, dropdown — kendi +
 * paylaşılan listeler; paylaşılan listeye de yazılabilir, "görebilen = yapabilen") · <b>Dosyalar</b>
 * (opsiyonel).
 *
 * <p><b>Yorum alanı yok</b> (19.10.2026, kullanıcı isteği): 14.10.2026'da eklenen ikinci metin alanı
 * kaldırıldı, etiket de "Görev"den "Notunuzu Yazınız"a döndü. Yorum Notlarım detayında yazılır (124).
 *
 * <p><b>Dosyalar burada yalnız TOPLANIR</b>, yüklenmez: yükleme için maddenin id'si gerekir. Seçilen
 * dosyalar `onSubmit`'in `QuickNoteArgs.files` alanıyla çağırana verilir ve madde oluşur oluşmaz yüklenir
 * (`useQuickAddNote`). Kalıp ve kabul listesi Görev Oluştur pop-up'ıyla aynıdır
 * (`StandaloneActionFormDialog` → {@link DOCUMENT_ACCEPT}).
 *
 * <p><b>Liste tek ise önceden seçilir</b> — tek seçenekli zorunlu bir dropdown her seferinde boşuna
 * bir tıklamadır. Hiç liste yoksa alan pasif çizilir ve nedeni söylenir (liste Notlarım ekranında
 * oluşturulur; burada liste açmak bu pop-up'ın işi değil).
 */
export default function QuickNoteDialog({ visible, submitting, onSubmit, onHide }: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset } = useZodForm(schema, {
    defaultValues: { name: '' },
  })
  const lists = useTodoListOptions(visible)
  const [listId, setListId] = useState<number | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const [showErrors, setShowErrors] = useState(false)

  const options = useMemo(() => lists.data ?? [], [lists.data])
  const noLists = lists.isSuccess && options.length === 0

  useEffect(() => {
    if (visible) {
      reset({ name: '' })
      setListId(null)
      // Önceki notun dosyaları bir sonraki "Not Ekle"ye taşınmamalı — yanlış maddeye iliştirilirdi.
      setFiles([])
      setShowErrors(false)
    }
  }, [visible, reset])

  // Tek liste → önceden seç. Kullanıcı seçimini ezmez (yalnız boşken).
  useEffect(() => {
    if (visible && listId == null && options.length === 1) {
      setListId(options[0].id)
    }
  }, [visible, listId, options])

  const onValid = ({ name }: { name: string }) => {
    if (listId == null) {
      setShowErrors(true)
      return
    }
    onSubmit({ listId, name: name.trim(), files })
  }

  return (
    <FormDialog
      visible={visible}
      header={t('todo.quickNote.title')}
      submitLabel={t('todo.quickNote.save')}
      submitting={submitting}
      submitDisabled={noLists}
      onSubmit={handleSubmit(onValid, () => setShowErrors(true))}
      onHide={onHide}
      width="42rem"
    >
      <div className="admin-form">
        <FormTextareaField
          control={control}
          name="name"
          label={t('todo.quickNote.note')}
          placeholder={t('todo.quickNote.notePlaceholder')}
          required
          rows={4}
          maxLength={NOTE_MAX}
        />

        <label className="act-field act-field--full">
          <span className="ui-field-label">
            {t('todo.quickNote.list')} <span className="ui-req">*</span>
          </span>
          <Dropdown
            value={listId}
            options={options}
            optionValue="id"
            optionLabel="name"
            itemTemplate={listLabel}
            valueTemplate={(opt: TodoList | null, props) => (opt ? listLabel(opt) : props.placeholder)}
            filter={options.length > 7}
            disabled={noLists}
            loading={lists.isLoading}
            placeholder={t('todo.quickNote.listPlaceholder')}
            emptyMessage={t('todo.quickNote.noLists')}
            className={showErrors && listId == null ? 'p-invalid' : undefined}
            onChange={(e) => setListId(e.value ?? null)}
          />
          {noLists && <span className="act-form-err">{t('todo.quickNote.noLists')}</span>}
          {showErrors && !noLists && listId == null && (
            <span className="act-form-err">{t('todo.quickNote.listRequired')}</span>
          )}
        </label>

        <div className="act-field act-field--full">
          <span className="ui-field-label">{t('todo.quickNote.files')}</span>
          <FileUpload
            files={files}
            onChange={setFiles}
            accept={DOCUMENT_ACCEPT}
            label={t('actions.detail.documents.add')}
            hint={t('actions.detail.documents.hint')}
          />
        </div>
      </div>
    </FormDialog>
  )
}
